import {
  Artwork,
  ObjectIdAsHexString,
} from "kreate-common/modules/business-types";
import { useRouter } from "next/router";
import React from "react";

import { ArtworkWindowContext } from "../contexts";
import { ArtworkWindow, fromArtworkWindow } from "../typing";

import { useArtworkList } from "@/modules/resources/hooks/useArtworkList";

function getPrevArtworkWindow(
  artworkWindow: ArtworkWindow | undefined
): ArtworkWindow | undefined {
  if (!artworkWindow) return undefined;
  const fetchParams = { ...artworkWindow.fetchParams };
  fetchParams.offset -= fetchParams.limit;
  if (fetchParams.offset < 0) return undefined;
  return { ...artworkWindow, fetchParams };
}

function getNextArtworkWindow(
  artworkWindow: ArtworkWindow | undefined
): ArtworkWindow | undefined {
  if (!artworkWindow) return undefined;
  const fetchParams = { ...artworkWindow.fetchParams };
  fetchParams.offset += fetchParams.limit;
  return { ...artworkWindow, fetchParams };
}

function head<T>(array?: T[]): T | undefined {
  return array?.[0];
}

function last<T>(array?: T[]): T | undefined {
  return array ? array[array.length - 1] : undefined;
}

export function useArtworkWindowApi({
  artworkId,
  artworkWindow,
}: {
  artworkId?: ObjectIdAsHexString;
  artworkWindow?: ArtworkWindow;
} = {}) {
  const artworkWindowFromContext = React.useContext(ArtworkWindowContext);
  artworkWindow ||= artworkWindowFromContext;
  const router = useRouter();

  const [currArtworkList] = useArtworkList(artworkWindow?.fetchParams);
  const shouldFetchPrevArtworkList =
    artworkId && head(currArtworkList?.artworks)?.artworkId === artworkId;
  const [prevArtworkList] = useArtworkList(
    shouldFetchPrevArtworkList
      ? getPrevArtworkWindow(artworkWindow)?.fetchParams
      : undefined
  );
  const shouldFetchNextArtworkList =
    artworkId && last(currArtworkList?.artworks)?.artworkId === artworkId;
  const [nextArtworkList] = useArtworkList(
    shouldFetchNextArtworkList
      ? getNextArtworkWindow(artworkWindow)?.fetchParams
      : undefined
  );

  const artworks = [
    ...(prevArtworkList?.artworks || []),
    ...(currArtworkList?.artworks || []),
    ...(nextArtworkList?.artworks || []),
  ];

  const visitArtwork = async (
    target: ObjectIdAsHexString | Pick<Artwork, "artworkId">,
    { openInNewTab }: { openInNewTab?: boolean } = {}
  ) => {
    const artworkId = typeof target === "string" ? target : target.artworkId;
    const search = new URLSearchParams();

    const containingArtworkWindow = //
      currArtworkList?.artworks.some((a) => a.artworkId === artworkId)
        ? artworkWindow
        : prevArtworkList?.artworks.some((a) => a.artworkId === artworkId)
        ? getPrevArtworkWindow(artworkWindow)
        : nextArtworkList?.artworks.some((a) => a.artworkId === artworkId)
        ? getNextArtworkWindow(artworkWindow)
        : undefined;

    if (containingArtworkWindow) {
      search.append(
        "artworkWindow",
        fromArtworkWindow(containingArtworkWindow)
      );
    }

    const href = `/artwork/${artworkId}${
      search.toString() ? "?" + search.toString() : ""
    }`;

    if (openInNewTab) {
      window.open(href, "_blank", "noreferrer");
      return true;
    } else {
      return await router.push(
        `/artwork/${artworkId}${
          search.toString() ? "?" + search.toString() : ""
        }`
      );
    }
  };

  const currIndex = artworks?.findIndex((a) => a.artworkId === artworkId);
  const prevArtwork = currIndex >= 0 ? artworks[currIndex - 1] : undefined;
  const currArtwork = currIndex >= 0 ? artworks[currIndex] : undefined;
  const nextArtwork = currIndex >= 0 ? artworks[currIndex + 1] : undefined;

  return { visitArtwork, prevArtwork, currArtwork, nextArtwork };
}
